import React, { useState } from "react";
import { collection, addDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { EMPTY_MANUAL_FORM, SPECIALTY_OPTIONS, MANUAL_ENTRY_STATE_OPTIONS } from "./constants";

// Manual registration for paper-enrollment contractors (signed a modified
// paper contract outside the website). Never sends the standard contract
// email; optionally sends an intro email with blank W-9/Direct Deposit forms.
const ManualEntryModal = ({ onClose, currentAdminEmail, showToast }) => {
    const [manualForm, setManualForm] = useState(EMPTY_MANUAL_FORM);
    const [manualSubmitting, setManualSubmitting] = useState(false);

    const handleManualFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setManualForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    };

    const handleClose = () => {
        if (manualSubmitting) return;
        onClose();
    };

    const handleManualSubmit = async (e) => {
        e.preventDefault();

        if (!manualForm.name.trim() || !manualForm.email.trim()) {
            showToast("Name and email are required", "error");
            return;
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(manualForm.email.trim())) {
            showToast("Please enter a valid email address", "error");
            return;
        }

        setManualSubmitting(true);
        try {
            const { sendIntroEmail, ...fields } = manualForm;

            const initialEntry = {
                id: Date.now().toString(),
                type: "note",
                category: "general",
                timestamp: new Date(),
                adminEmail: currentAdminEmail,
                content: sendIntroEmail
                    ? "Manually registered as a paper enrollment. Intro email with W-9 & Direct Deposit forms queued — no contract email sent."
                    : "Manually registered as a paper enrollment. No automated emails sent.",
            };

            await addDoc(collection(db, "medical_providers"), {
                ...fields,
                name: fields.name.trim(),
                email: fields.email.trim(),
                enrollmentType: "paper",
                source: "manual_admin",
                enteredBy: currentAdminEmail,
                sendIntroEmail,
                termsAccepted: false,
                timestamp: new Date(),
                status: "new",
                emailSent: false,
                activityLog: [initialEntry],
            });

            showToast(sendIntroEmail
                ? "Contractor added — intro email with forms will be sent shortly"
                : "Contractor added — no emails sent");
            onClose();
        } catch (error) {
            console.error("Error adding contractor manually:", error);
            showToast("Failed to add contractor. Please try again.", "error");
        } finally {
            setManualSubmitting(false);
        }
    };

    return (
        <div
            className="modal-overlay"
            onClick={handleClose}>
            <div
                className="modal-content manual-entry-modal"
                onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>📄 Add Contractor Manually</h2>
                    <button
                        className="close-btn"
                        onClick={handleClose}>
                        ×
                    </button>
                </div>
                <div className="modal-body">
                    <div className="manual-entry-notice">
                        For contractors who signed a <strong>paper contract</strong> outside the website.
                        The standard contract email will <strong>not</strong> be sent.
                    </div>
                    <form onSubmit={handleManualSubmit}>
                        <div className="manual-form-grid">
                            <div className="manual-form-group">
                                <label htmlFor="manual-name">Full Name *</label>
                                <input id="manual-name" type="text" name="name" value={manualForm.name} onChange={handleManualFormChange} required placeholder="Contractor's full name" />
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-email">Email *</label>
                                <input id="manual-email" type="email" name="email" value={manualForm.email} onChange={handleManualFormChange} required placeholder="Email address" />
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-phone">Phone</label>
                                <input id="manual-phone" type="tel" name="phone" value={manualForm.phone} onChange={handleManualFormChange} placeholder="Phone number" />
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-specialty">Specialty</label>
                                <select id="manual-specialty" name="specialty" value={manualForm.specialty} onChange={handleManualFormChange}>
                                    <option value="">Select specialty...</option>
                                    {SPECIALTY_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-practiceName">Practice Name</label>
                                <input id="manual-practiceName" type="text" name="practiceName" value={manualForm.practiceName} onChange={handleManualFormChange} placeholder="Practice name" />
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-dbaName">DBA Name</label>
                                <input id="manual-dbaName" type="text" name="dbaName" value={manualForm.dbaName} onChange={handleManualFormChange} placeholder="DBA name (if applicable)" />
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-location">Practice State</label>
                                <select id="manual-location" name="location" value={manualForm.location} onChange={handleManualFormChange}>
                                    <option value="">Select state...</option>
                                    {MANUAL_ENTRY_STATE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-licenseNumber">License Number</label>
                                <input id="manual-licenseNumber" type="text" name="licenseNumber" value={manualForm.licenseNumber} onChange={handleManualFormChange} placeholder="License number" />
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-address">Practice Address</label>
                                <input id="manual-address" type="text" name="address" value={manualForm.address} onChange={handleManualFormChange} placeholder="Street address" />
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-city">City</label>
                                <input id="manual-city" type="text" name="city" value={manualForm.city} onChange={handleManualFormChange} placeholder="City" />
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-zip">ZIP Code</label>
                                <input id="manual-zip" type="text" name="zip" value={manualForm.zip} onChange={handleManualFormChange} placeholder="ZIP code" />
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-practicePhone">Practice Phone</label>
                                <input id="manual-practicePhone" type="tel" name="practicePhone" value={manualForm.practicePhone} onChange={handleManualFormChange} placeholder="Practice phone" />
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-npi">NPI Number</label>
                                <input id="manual-npi" type="text" name="npi" value={manualForm.npi} onChange={handleManualFormChange} placeholder="10-digit NPI number" />
                            </div>
                            <div className="manual-form-group">
                                <label htmlFor="manual-taxId">Tax ID</label>
                                <input id="manual-taxId" type="text" name="taxId" value={manualForm.taxId} onChange={handleManualFormChange} placeholder="Tax ID number" />
                            </div>
                        </div>
                        <div className="manual-form-group full-width">
                            <label htmlFor="manual-message">Notes / Practice Description</label>
                            <textarea id="manual-message" name="message" rows="3" value={manualForm.message} onChange={handleManualFormChange} placeholder="Notes about this contractor — e.g. details of the modified paper contract..." />
                        </div>
                        <label className="manual-intro-checkbox">
                            <input
                                type="checkbox"
                                name="sendIntroEmail"
                                checked={manualForm.sendIntroEmail}
                                onChange={handleManualFormChange}
                            />
                            <span>
                                Send intro email with blank <strong>W-9</strong> &amp; <strong>Direct Deposit</strong> forms attached
                                <span className="manual-email-note">No contract or agreement is ever emailed for paper enrollments.</span>
                            </span>
                        </label>
                        <div className="manual-form-footer">
                            <button type="button" className="manual-cancel-btn" onClick={handleClose} disabled={manualSubmitting}>
                                Cancel
                            </button>
                            <button type="submit" className="manual-submit-btn" disabled={manualSubmitting}>
                                {manualSubmitting ? "Adding..." : "➕ Add Contractor"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ManualEntryModal;
